/**
 * SubmissionProgress.js
 *
 * The host's screen during the submission step (usually on a projector).
 * Shows each team that played: nothing to choose, still choosing, or submitted.
 * The host shows the results once every team with extra items has submitted, or earlier
 * (teams that haven't chosen get their best set picked for them).
 * If no team bought more than it submits, the results come up straight away.
 */

import React, { useEffect, useRef, useState } from "react";
import { Trophy } from "lucide-react";
import toast from "react-hot-toast";
import { getAuctionSettings } from "../assets/Rules";
import { getTeamColor, sortTeams } from "../assets/teamColors";
import { getRoomLibrary, getTeamItems } from "../library/libraries";
import { showResults } from "../helpers/auctionLobby";
import { scoreTeam } from "../helpers/scoring";

const SubmissionProgress = ({ room, teams }) => {
    const settings = getAuctionSettings(room);
    const library = getRoomLibrary(room);
    const [isShowing, setIsShowing] = useState(false);
    const autoShown = useRef(false);

    const playingTeams = sortTeams(teams).filter((team) => team.members?.length > 0);
    const statuses = playingTeams.map((team) => {
        const itemCount = getTeamItems(team).length;
        const hasChoice = itemCount > settings.itemsToSubmit;
        const submitted = hasChoice && scoreTeam(team, settings, library).chosenByTeam;
        return { team, itemCount, hasChoice, submitted };
    });
    const choosing = statuses.filter((status) => status.hasChoice && !status.submitted);
    const anyChoice = statuses.some((status) => status.hasChoice);

    const handleShowResults = async (confirmFirst) => {
        if (confirmFirst && !window.confirm(`${choosing.map((s) => s.team.name).join(", ")} haven't submitted yet. Their best set will be picked for them. Show the results?`)) return;
        setIsShowing(true);
        if (!(await showResults(room.id))) {
            toast.error("Couldn't show the results");
            setIsShowing(false);
        }
    };

    // Nobody has anything to choose: straight to the results
    useEffect(() => {
        if (!anyChoice && !autoShown.current) {
            autoShown.current = true;
            showResults(room.id);
        }
    }, [anyChoice, room.id]);

    return (
        <div className="w-full max-w-[110rem] mx-auto px-6 md:px-12 pt-24 pb-12 text-white">
            <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-10">
                <div>
                    <p className="text-2xl md:text-3xl text-yellow font-semibold">The auction is over</p>
                    <h1 className="mt-2 font-extrabold leading-none text-[clamp(3rem,8vw,7rem)]">Submissions</h1>
                    <p className="mt-3 text-2xl md:text-3xl text-zinc-400">
                        Teams with more than {settings.itemsToSubmit} items are picking the {settings.itemsToSubmit} that count, on their phones.
                    </p>
                </div>

                <div className="flex flex-col items-stretch xl:items-end gap-3">
                    <button
                        onClick={() => handleShowResults(choosing.length > 0)}
                        disabled={isShowing}
                        className="inline-flex items-center justify-center gap-4 rounded-3xl bg-gradient-to-br from-yellow via-peach to-coral px-14 py-8 text-4xl md:text-5xl font-extrabold text-black shadow-2xl transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40"
                    >
                        <Trophy className="w-10 h-10 md:w-12 md:h-12" />
                        {isShowing ? "Showing..." : "Show results"}
                    </button>
                    <span className="text-xl text-zinc-400 text-center xl:text-right">
                        {choosing.length === 0 ? "Everyone has submitted" : `Waiting for ${choosing.length} team${choosing.length === 1 ? "" : "s"}`}
                    </span>
                </div>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-6">
                {statuses.map(({ team, itemCount, hasChoice, submitted }) => (
                    <div key={team.id} className="rounded-3xl border border-zinc-800 bg-zinc-950 overflow-hidden">
                        <div className={`h-3 ${getTeamColor(team).bg}`} />
                        <div className="p-6">
                            <div className="text-3xl font-bold">{team.name}</div>
                            <div className="mt-2 text-xl text-zinc-400">{itemCount} items bought</div>
                            <div className={`mt-5 text-2xl font-semibold ${!hasChoice ? "text-zinc-500" : submitted ? "text-green" : "text-peach"}`}>
                                {!hasChoice ? "Nothing to choose" : submitted ? "Submitted ✓" : "Choosing…"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SubmissionProgress;
